import React, { useEffect, useMemo, useState } from "react";
import "../styles/PromoPopup.scss";

interface PromoItem {
  id: string;
  promo_title: string;
  promo_poster: string;
  promo_icon: string;
  promo_link: string;
  promo_start: string;
  promo_end: string;
}

const PromoPopup: React.FC = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(true);
  const [showReopenIcon, setShowReopenIcon] = useState(true);
  const [promos, setPromos] = useState<PromoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [timeLeft, setTimeLeft] = useState("");
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const fetchPromos = async () => {
      try {
        const response = await fetch("/resources/promo.json");
        const data = await response.json();
        setPromos(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Failed to load promo.json:", error);
        setPromos([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPromos();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const activePromo = useMemo(() => {
    if (!promos.length) return null;

    return (
      promos.find((promo) => {
        const start = new Date(`${promo.promo_start}T00:00:00`).getTime();
        const end = new Date(`${promo.promo_end}T23:59:59`).getTime();

        return now >= start && now <= end;
      }) || null
    );
  }, [promos, now]);

  useEffect(() => {
    if (!activePromo) {
      setTimeLeft("");
      return;
    }

    const end = new Date(`${activePromo.promo_end}T23:59:59`).getTime();
    const distance = end - now;

    if (distance <= 0) {
      setTimeLeft("");
      return;
    }

    const totalSeconds = Math.floor(distance / 1000);
    const totalHours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const formattedTime = `${String(totalHours).padStart(2, "0")}:${String(
      minutes
    ).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;

    setTimeLeft(formattedTime);
  }, [activePromo, now]);

  const handleClosePopup = () => {
    setIsPopupOpen(false);
    setShowReopenIcon(true);
  };

  const handleOpenPopup = () => {
    setIsPopupOpen(true);
  };

  const handleHideIcon = () => {
    setShowReopenIcon(false);
  };

  if (loading || !activePromo) return null;

  return (
    <>
      {isPopupOpen && (
        <div className="promo-popup-overlay">
          <div className="promo-popup">
            <button className="promo-close-btn" onClick={handleClosePopup}>
              ×
            </button>

            <a
              href={activePromo.promo_link}
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src={activePromo.promo_poster}
                alt={activePromo.promo_title}
                className="promo-popup-image"
              />
            </a>
          </div>
        </div>
      )}

      {!isPopupOpen && showReopenIcon && (
        <div className="promo-reopen-wrapper">
          <div className="promo-reopen-content">
            <button
              className="promo-reopen-btn"
              onClick={handleOpenPopup}
              aria-label={`Open ${activePromo.promo_title}`}
            >
              <img
                src={activePromo.promo_icon}
                alt={activePromo.promo_title}
                className="promo-reopen-image"
              />
            </button>

            <div className="promo-timer">{timeLeft}</div>
          </div>

          <button className="promo-hide-icon-btn" onClick={handleHideIcon}>
            ×
          </button>
        </div>
      )}
    </>
  );
};

export default PromoPopup;